(() => {
  if (window.InkArtworkOrderPolicy) return;
  const configNode = document.getElementById('ink-artwork-order-policy-config');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const requiredHandles = new Set(config.requiredHandles || []);
  const variantHandles = new Set(config.variantHandles || []);
  const root = window.Shopify?.routes?.root || '/';
  const cartUrl = root + 'cart';
  const requiredFields = ['Artwork ID', '_Artwork version', '_Artwork GCS object'];
  const messages = config.messages || {};
  const text = (key, fallback) => messages[key] || fallback;
  let checking = false;
  let resuming = false;
  let refreshTimer;

  const handleFor = item => {
    if (typeof item?.handle === 'string') return item.handle;
    try {
      return new URL(item?.url || item?.productUrl || '', window.location.origin).pathname.match(/\/products\/([^/]+)/)?.[1] || '';
    } catch {
      return '';
    }
  };

  const requiresArtwork = item => {
    const handle = handleFor(item);
    if (Object.hasOwn(config.cartRequirements || {}, handle)) return config.cartRequirements[handle];
    if (handle === config.pageHandle && config.pageRequiresArtwork) return true;
    return requiredHandles.has(handle)
      || variantHandles.has(handle)
      || item.properties?.['_Roll artwork required'] === 'yes';
  };

  const legacyUpload = properties => Object.entries(properties || {}).some(([key, value]) => {
    if (!/^upload(?:_\d+)?$/i.test(key) || typeof value !== 'string') return false;
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && (url.hostname === 'cloudlift.app' || url.hostname.endsWith('.cloudlift.app')
        || ((url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.myshopify.com')) && url.pathname.includes('/uploads/')));
    } catch { return false; }
  });
  const hasArtwork = item => {
    const properties = item.properties || {};
    if (requiredFields.some(field => properties[field])) {
      return requiredFields.every(field => typeof properties[field] === 'string' && !!properties[field].trim());
    }
    return legacyUpload(properties);
  };
  const missingArtwork = cart => {
    if (!Array.isArray(cart?.items)) throw new Error('Invalid cart response');
    return cart.items.filter(item => requiresArtwork(item) && !hasArtwork(item));
  };

  const clearNotice = () => document.getElementById('ink-artwork-cart-warning')?.remove();

  const showNotice = (items, failed = false, focus = false) => {
    clearNotice();
    const notice = document.createElement('div');
    notice.id = 'ink-artwork-cart-warning';
    notice.setAttribute('role', 'alert');
    notice.setAttribute('tabindex', '-1');
    notice.style.cssText = 'margin:16px 0;padding:16px;border:1px solid #b43b32;border-radius:8px;background:#fff8f6;color:#78231d;';
    const message = document.createElement('p');
    message.textContent = failed
      ? text('failed', 'We could not check your artwork. Please try checkout again after your cart finishes updating.')
      : text('missing', 'Please finish the artwork for each item below before checkout. A design attached to another item does not apply automatically.');
    notice.appendChild(message);
    if (!failed) {
      const list = document.createElement('ul');
      items.forEach(item => {
        const entry = document.createElement('li');
        const label = document.createElement('span');
        label.textContent = (item.product_title || item.title || 'Custom product') + ' × ' + item.quantity + ' — ';
        const link = document.createElement('a');
        const handle = handleFor(item);
        const panel = [...document.querySelectorAll('ink-roll-cart-notes')].find(node => node.dataset.lineKey === item.key);
        link.textContent = text('open', 'Open product and upload artwork');
        link.style.textDecoration = 'underline';
        link.href = panel ? cartUrl : /^[a-z0-9-]+$/.test(handle) ? root + 'products/' + handle : cartUrl;
        if (panel) link.addEventListener('click', event => {
          event.preventDefault();
          panel.requireArtwork?.();
        });
        entry.append(label, link);
        if (!panel) {
          const help = document.createElement('span');
          help.textContent = '. ' + text('repair', 'After adding the configured product, remove this incomplete item from your cart.');
          entry.appendChild(help);
        }
        list.appendChild(entry);
      });
      notice.appendChild(list);
    }
    const host = document.getElementById('main-cart-items') || document.getElementById('MainContent') || document.querySelector('main') || document.body;
    host.prepend(notice);
    if (focus) {
      notice.focus({ preventScroll: true });
      notice.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const readCart = async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(cartUrl + '.js', {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: controller.signal
      });
      if (!response.ok) throw new Error('Cart request failed');
      return await response.json();
    } finally {
      clearTimeout(timeout);
    }
  };

  const isCheckout = node => {
    if (node?.matches('button[name="checkout"],input[name="checkout"]')) return true;
    if (!node?.matches('a[href]')) return false;
    try {
      const url = new URL(node.href, window.location.origin);
      return url.origin === window.location.origin && /\/(checkout|checkouts)(\/|$)/.test(url.pathname);
    } catch {
      return false;
    }
  };

  const checkoutTarget = event => {
    if (event.type === 'submit') {
      const target = event.submitter || event.target.querySelector('[name="checkout"]');
      return isCheckout(target) ? target : null;
    }
    const target = event.target.closest?.('button[name="checkout"],input[name="checkout"],a[href]');
    return isCheckout(target) ? target : null;
  };

  const guardCheckout = async event => {
    if (resuming) return;
    const original = checkoutTarget(event);
    if (!original) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checking) return;
    checking = true;
    const originalForm = original.form;
    try {
      const helper = window.Alpine?.store?.('xCartHelper');
      await helper?.waitForCartUpdate?.();
      const panels = [...document.querySelectorAll('ink-roll-cart-notes')];
      for (const panel of panels) {
        if (panel.readyForCheckout && !panel.readyForCheckout()) {
          panel.requireArtwork?.();
          return;
        }
      }
      const missing = missingArtwork(await readCart());
      if (missing.length) {
        showNotice(missing, false, true);
        return;
      }
      clearNotice();
      const currentForm = originalForm?.isConnected ? originalForm : document.getElementById(originalForm?.getAttribute('id') || '');
      const current = original.isConnected ? original : document.getElementById(original.id || '') || currentForm?.querySelector('[name="checkout"]');
      if (!current) {
        showNotice([], true, true);
        return;
      }
      resuming = true;
      current.click();
    } catch {
      showNotice([], true, true);
    } finally {
      resuming = false;
      checking = false;
    }
  };

  const refreshNotice = async () => {
    if (window.location.pathname.replace(/\/$/, '') !== cartUrl.replace(/\/$/, '') || checking) return;
    try {
      const missing = missingArtwork(await readCart());
      if (checking) return;
      if (missing.length) showNotice(missing);
      else clearNotice();
    } catch {
      return;
    }
  };

  const getProductForm = source => {
    const native = window.getInkArtworkForm?.(source) || source;
    if (native !== source || !source?.getAttribute('id')?.startsWith('product-form-sticky-')) return native;
    return [...document.querySelectorAll('form[data-ink-product-handle]')].find(form => form !== source
      && !form.getAttribute('id')?.startsWith('product-form-sticky-') && form.dataset.inkProductHandle === source.dataset.inkProductHandle
      && form.querySelector('input[type="file"],input[name="properties[upload]"]')) || source;
  };
  const validateForm = (source, event) => {
    const form = getProductForm(source);
    if (!form) return false;
    const handle = form.dataset.inkProductHandle || source?.dataset.inkProductHandle || '';
    const required = form.dataset.inkArtworkRequired === 'true' || source?.dataset.inkArtworkRequired === 'true'
      || requiresArtwork({handle});
    if (!required) return true;
    const roots = [...form.querySelectorAll('[data-ink-artwork-upload]')];
    const properties = {};
    for (const [key, value] of new FormData(form)) {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (match && typeof value === 'string') properties[match[1]] = value;
    }
    const native = roots.length > 0 || form.querySelector('[data-upload-input]');
    const valid = native
      ? !!window.validateInkArtworkUpload?.(form, event) && hasArtwork({properties})
      : legacyUpload(properties);
    if (valid) { form.querySelector('[data-ink-upload-required-notice]')?.remove(); return true; }
    event?.preventDefault();
    event?.stopImmediatePropagation();
    if (!native && handle !== config.pageHandle && !form.querySelector('input[type="file"],.upload-lift')) {
      if (/^[a-z0-9-]+$/.test(handle)) window.location.assign(root + 'products/' + handle);
      return false;
    }
    let notice = form.querySelector('[data-ink-upload-required-notice]');
    if (!notice) {
      notice = document.createElement('p');
      notice.dataset.inkUploadRequiredNotice = '';
      notice.setAttribute('role','alert');
      form.prepend(notice);
    }
    notice.textContent = text('upload', 'Please upload your artwork and wait for the upload to finish before adding this product.');
    return false;
  };
  const guardAdd = event => {
    const button = event.type === 'click' ? event.target.closest?.('button[name="add"],input[name="add"]') : event.submitter;
    const form = event.type === 'submit' ? event.target : button?.form;
    if (form?.matches('form[data-type="add-to-cart-form"],form[data-ink-artwork-required]') && button?.name !== 'checkout') validateForm(form,event);
  };
  window.InkArtworkOrderPolicy = { requiresArtwork, missingArtwork, hasArtwork, validateForm, getProductForm, messages, readCart, showNotice };
  window.addEventListener('click', guardAdd, true);
  window.addEventListener('submit', guardAdd, true);
  window.addEventListener('click', guardCheckout, true);
  window.addEventListener('submit', guardCheckout, true);
  document.addEventListener('eurus:cart:items-changed', () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshNotice, 150);
  });
  refreshNotice();
})();
